<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { api, post } from '../services/api'
import { useSessionStore } from '../stores/session'

const route = useRoute()
const router = useRouter()
const session = useSessionStore()
const mode = ref('friends')
const playerName = ref('')
const style = ref('standard')
const customStyle = ref('')
const roomName = ref('')
const configuration = ref(null)
const error = ref('')
const busy = ref(false)
const accessType = ref('free')
const access = computed(() => configuration.value?.game_access)
const subscription = computed(() => access.value?.subscription)
const styles = computed(() => (configuration.value?.styles || [{ id: 'standard', label: 'Стандартный' }])
  .filter(item => item.id !== 'custom' || subscription.value?.active))
// Without a subscription the custom style is shown as a locked tile that leads to /buy.
const customLocked = computed(() => configuration.value?.styles?.some(item => item.id === 'custom') && !subscription.value?.active)
// Example pictures from earlier games that hint at each style; unknown styles fall back to the cat.
const styleArt = {
  standard: '/assets/kids/kittens-original.webp',
  anime: '/assets/kids/puppies-3.webp',
  memes: '/assets/company/pig-original.webp',
  'old-cinema': '/assets/kids/dumplings-original.webp',
  rap: '/assets/company/pumpkin-original.webp',
  comic: '/assets/company/pumpkin-comic.webp',
  watercolor: '/assets/kids/angel-sunny.webp',
  'pixel-art': '/assets/company/caveman-original.webp',
  '3d': '/assets/kids/gummy.webp',
  custom: '/assets/company/mermaid-replica.webp'
}
const artFor = id => styleArt[id] || '/assets/original.webp'
const selectedStyle = computed(() => styles.value.find(item => item.id === style.value) || styles.value[0])
const displayName = computed(() => playerName.value.trim())
const modes = {
  friends: { title: 'С друзьями', icon: '👥', art: '/assets/company/crab-party.webp', perks: ['Нужно от двух игроков', 'Позовёте по ссылке или коду', 'Каждый по очереди становится автором'] },
  ai: { title: 'Один на один с ИИ', icon: '🤖', art: '/assets/kids/catstronaut-replica.webp', perks: ['Начнёте сразу, ждать никого не нужно', 'ИИ сам придумывает сцены', 'И сам отвечает — попробуйте его обойти'] }
}
const nextSteps = computed(() => mode.value === 'ai'
  ? [['▶', 'Начинаете сразу', 'Комната создаётся только для вас'], ['✦', 'ИИ загадывает', 'Придумывает сцену и рисует оригинал'], ['✎', 'Вы повторяете', 'Описываете картинку другими словами'], ['★', 'Сравниваете счёт', 'Кто ближе к оригиналу — вы или ИИ']]
  : [['▣', 'Создаёте комнату', 'И становитесь организатором'], ['♡', 'Зовёте друзей', 'По ссылке или коду комнаты'], ['✦', 'Автор загадывает', 'ИИ рисует оригинал по описанию'], ['★', 'Все повторяют', 'И получают очки за сходство']])
// Only real options are offered; a lone option is shown as a note instead of a choice.
const accessOptions = computed(() => {
  if (!access.value) return []
  return [
    access.value.free_available && { value: 'free', title: 'Бесплатная', detail: 'игра на сегодня', label: 'Бесплатная игра на сегодня' },
    subscription.value?.remaining && { value: 'subscription', title: 'По подписке', detail: `осталось ${subscription.value.remaining} из ${subscription.value.games_per_month}`, label: `По подписке · осталось ${subscription.value.remaining} из ${subscription.value.games_per_month}` },
    access.value.credits && { value: 'paid', title: 'Из пакета', detail: `осталось ${access.value.credits}`, label: `Из пакета · осталось ${access.value.credits}` }
  ].filter(Boolean)
})
const selectedAccess = computed(() => accessOptions.value.find(option => option.value === accessType.value))
const outOfGames = computed(() => access.value && !accessOptions.value.length)
const providerDown = computed(() => configuration.value?.provider && !configuration.value.provider.ready)
const canCreate = computed(() => session.ready && playerName.value.trim() && (style.value !== 'custom' || customStyle.value.trim()) && !busy.value)

async function refreshAccess() {
  try { configuration.value = await api('/configuration') }
  catch (reason) { error.value = reason.message }
  if (!accessOptions.value.some(option => option.value === accessType.value)) accessType.value = accessOptions.value[0]?.value || 'free'
}
async function initializeAccess() {
  if (session.ready) await refreshAccess()
}
onMounted(() => {
  // /solo links here with ?mode=ai; applied after hydration because the prerendered page has no query.
  if (route.query.mode === 'ai') mode.value = 'ai'
  playerName.value = localStorage.getItem('promptguess-name') || ''
  initializeAccess()
})
watch(() => session.ready, initializeAccess)
watch(() => session.user?.guest, () => refreshAccess())

async function createGame() {
  busy.value = true; error.value = ''
  const name = roomName.value.trim() || (mode.value === 'ai' ? `${playerName.value.trim()} против ИИ` : `Игра ${playerName.value.trim()}`)
  try {
    const game = await post('/games', { display_name: playerName.value.trim(), game: { name, image_style: style.value, access_type: accessType.value, settings: { custom_style: customStyle.value, ai_opponent: mode.value === 'ai' } } })
    localStorage.setItem('promptguess-name', playerName.value.trim())
    await router.push({ name: game.status === 'lobby' ? 'lobby' : 'play', params: { id: game.id } })
  } catch (reason) { error.value = reason.message }
  finally { busy.value = false }
}
</script>

<template>
  <main class="create-page">
    <section class="create-hero" aria-labelledby="create-title">
      <div class="create-hero-copy">
        <RouterLink class="back-link" to="/">← На главную</RouterLink>
        <h1 id="create-title">Новая игра.<br /><span>Ваши правила.</span></h1>
        <p>Выберите, с кем играть и в каком стиле ИИ будет рисовать сцены. Уже есть код комнаты? <RouterLink to="/join">Войти&nbsp;по&nbsp;коду&nbsp;→</RouterLink></p>
        <div class="create-hero-proof"><span>✦ Одна игра в день бесплатно</span><span>♡ Можно без регистрации</span><span>✳ Рисовать не нужно</span></div>
      </div>
      <div class="create-hero-scene" aria-hidden="true">
        <span class="create-doodle create-doodle-star">✳</span>
        <Transition name="create-swap" mode="out-in">
          <figure :key="selectedStyle.id" class="create-polaroid create-polaroid-main" :class="`art-${selectedStyle.id}`">
            <img :src="artFor(selectedStyle.id)" alt="" />
            <figcaption>Стиль: {{ selectedStyle.label }}</figcaption>
          </figure>
        </Transition>
        <figure class="create-polaroid create-polaroid-side">
          <img :src="modes[mode].art" alt="" />
          <figcaption>{{ mode === 'ai' ? 'Соперник — ИИ' : 'Ваша компания' }}</figcaption>
        </figure>
        <div class="create-player-chip"><b>{{ (displayName || '?')[0].toUpperCase() }}</b><span><strong>{{ displayName || 'Ваше имя' }}</strong><small>{{ mode === 'ai' ? 'против ИИ' : 'организатор' }}</small></span></div>
        <span class="create-doodle create-doodle-planet">✦</span>
      </div>
    </section>

    <form class="create-layout" :aria-busy="busy" @submit.prevent="createGame">
      <div class="create-steps">
        <fieldset class="create-step" aria-labelledby="create-mode-title">
          <div class="create-step-head"><b>01</b><div><h2 id="create-mode-title">С кем играете?</h2><p>Режим можно выбрать заново для каждой игры</p></div></div>
          <div class="create-modes">
            <label v-for="(item, key) in modes" :key="key" class="create-mode" :class="[`create-mode-${key}`, { selected: mode === key }]">
              <input v-model="mode" type="radio" name="mode" :value="key" />
              <span class="create-mode-art"><img loading="lazy" :src="item.art" alt="" /><span class="create-mode-icon" aria-hidden="true">{{ item.icon }}</span></span>
              <span class="create-mode-copy">
                <strong>{{ item.title }}</strong>
                <ul><li v-for="perk in item.perks" :key="perk"><span aria-hidden="true">✓</span>{{ perk }}</li></ul>
              </span>
              <span class="create-mode-badge" aria-hidden="true">✓ Выбрано</span>
            </label>
          </div>
        </fieldset>

        <fieldset class="create-step" aria-labelledby="create-name-title">
          <div class="create-step-head"><b>02</b><div><h2 id="create-name-title">Как вас зовут?</h2><p>Имя увидят другие игроки и таблица очков</p></div></div>
          <label class="create-name-field">
            <span class="create-avatar" aria-hidden="true">{{ (displayName || '?')[0].toUpperCase() }}</span>
            <span class="visually-hidden">Ваше имя</span>
            <input v-model="playerName" maxlength="24" autocomplete="nickname" placeholder="Например, Аня" required />
          </label>
          <details v-if="mode === 'friends'" class="create-room-name">
            <summary>Название комнаты <span>необязательно</span></summary>
            <label class="ui-field"><span class="visually-hidden">Название комнаты</span><input v-model="roomName" maxlength="80" placeholder="Например, Пятничный космос" /><small>Увидят все участники. Если оставить пустым, назовём по вашему имени.</small></label>
          </details>
        </fieldset>

        <fieldset class="create-step" aria-labelledby="create-style-title">
          <div class="create-step-head"><b>03</b><div><h2 id="create-style-title">Стиль картинок</h2><p>В этом стиле ИИ нарисует все сцены игры</p></div></div>
          <div class="create-styles">
            <label v-for="item in styles" :key="item.id" class="create-style" :class="[`art-${item.id}`, { selected: style === item.id }]">
              <input v-model="style" type="radio" name="style" :value="item.id" />
              <img loading="lazy" :src="artFor(item.id)" alt="" />
              <span>{{ item.label }}</span>
            </label>
            <RouterLink v-if="customLocked" class="create-style create-style-locked art-custom" to="/buy#subscription">
              <img loading="lazy" :src="artFor('custom')" alt="" />
              <span>Свой стиль<small>🔒 с подпиской</small></span>
            </RouterLink>
          </div>
          <label v-if="style === 'custom'" class="ui-field create-custom-style">Опишите свой стиль<textarea v-model="customStyle" maxlength="500" rows="3" placeholder="Например, акварель в пастельных тонах" required /></label>
        </fieldset>
      </div>

      <aside class="create-summary" aria-labelledby="create-summary-title">
        <h2 id="create-summary-title">Ваша игра</h2>
        <dl class="create-receipt">
          <div><dt>Режим</dt><dd>{{ modes[mode].icon }} {{ modes[mode].title }}</dd></div>
          <div><dt>Игрок</dt><dd :class="{ empty: !displayName }">{{ displayName || 'не указано' }}</dd></div>
          <div><dt>Стиль</dt><dd>{{ selectedStyle.label }}</dd></div>
          <div v-if="mode === 'friends' && roomName.trim()"><dt>Комната</dt><dd>{{ roomName.trim() }}</dd></div>
        </dl>

        <fieldset v-if="accessOptions.length > 1" class="create-access">
          <legend>Какую игру использовать?</legend>
          <div>
            <label v-for="option in accessOptions" :key="option.value" :class="{ selected: accessType === option.value }">
              <input v-model="accessType" type="radio" name="access" :value="option.value" />
              <strong>{{ option.title }}</strong><small>{{ option.detail }}</small>
            </label>
          </div>
        </fieldset>
        <p v-else-if="accessOptions.length === 1" class="create-access-single"><span aria-hidden="true">✦</span>{{ accessOptions[0].label }}</p>

        <p v-if="providerDown" class="create-warning" role="status">Сейчас картинки не создаются. Попробуйте чуть позже.</p>
        <p v-if="error" class="home-error" role="alert">{{ error }}</p>
        <template v-if="outOfGames">
          <p class="create-warning">Бесплатная игра на сегодня уже использована. Новая появится завтра — или можно взять пакет игр.</p>
          <RouterLink class="button button-primary create-submit" to="/buy">Купить игры или подписку <span aria-hidden="true">→</span></RouterLink>
        </template>
        <template v-else>
          <button class="button button-primary create-submit" type="submit" :disabled="!canCreate">
            {{ busy ? 'Создаём…' : mode === 'ai' ? 'Начать игру с ИИ' : 'Создать комнату' }} <span aria-hidden="true">→</span>
          </button>
          <p v-if="!displayName" class="create-summary-note">Осталось указать имя</p>
          <p v-else-if="selectedAccess && accessType !== 'free' && mode === 'friends'" class="create-summary-note">Друзья войдут без расхода своей бесплатной игры</p>
          <p v-else-if="!access" class="create-summary-note">Одна игра в день бесплатно, даже без регистрации</p>
        </template>
        <RouterLink v-if="!subscription?.active" class="create-upsell" to="/buy">
          <img loading="lazy" src="/assets/subscription-astronaut-cat.webp" alt="" />
          <span><strong>Играете часто?</strong><small>Подписка: игры каждый месяц и свой стиль</small></span>
          <b aria-hidden="true">→</b>
        </RouterLink>
      </aside>
    </form>

    <section class="create-next" aria-labelledby="create-next-title">
      <div class="create-next-heading"><h2 id="create-next-title">Что будет дальше?</h2><p>{{ mode === 'ai' ? 'Игра начнётся сразу после создания.' : 'Комната откроется, как только вы её создадите.' }}</p></div>
      <ol><li v-for="([icon, title, text]) in nextSteps" :key="title"><span class="create-next-icon" aria-hidden="true">{{ icon }}</span><strong>{{ title }}</strong><small>{{ text }}</small></li></ol>
    </section>
  </main>
</template>
